import { lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

const MOBILE_QUERY = '(max-width: 749px)';
const DRAWER_QUERY = '(max-width: 989px)';

class DarkrimCollection extends HTMLElement {
  #applyTimer = 0;
  #openFrame = 0;
  #mediaQuery = window.matchMedia(MOBILE_QUERY);
  #drawerQuery = window.matchMedia(DRAWER_QUERY);
  /** @type {HTMLElement | null} */
  #drawerTrigger = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
    this.#drawerQuery.addEventListener('change', this.#onDrawerBreakpoint);
    window.addEventListener('popstate', this.#onPopState);
    const drawer = this.#drawer;
    if (drawer instanceof HTMLDialogElement) {
      drawer.addEventListener('close', this.#onDrawerClosed);
    }
    this.#syncPriceRanges();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#drawerQuery.removeEventListener('change', this.#onDrawerBreakpoint);
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('resize', this.#syncDrawerTop);
    document.removeEventListener('pointerdown', this.#onSortPointerDown);
    window.clearTimeout(this.#applyTimer);
    if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
    const drawer = this.#drawer;
    if (drawer instanceof HTMLDialogElement) {
      drawer.removeEventListener('close', this.#onDrawerClosed);
      unlockScroll(drawer);
    }
  }

  get #isMobile() {
    return this.#mediaQuery.matches;
  }

  get #drawer() {
    return this.querySelector('[data-darkrim-coll-drawer]');
  }

  #onDrawerBreakpoint = () => {
    if (!this.#drawerQuery.matches) this.#closeDrawer();
    else if (this.#drawer?.open) this.#syncDrawerTop();
  };

  #syncDrawerTop = () => {
    const header = document.querySelector('#header-component, header-component');
    let top = 0;
    if (header instanceof HTMLElement) {
      top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    } else {
      const fallback = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
      top = Number.isFinite(fallback) ? fallback : 60;
    }
    this.style.setProperty('--darkrim-coll-drawer-top', `${top}px`);
  };

  #onPopState = () => {
    this.#load(window.location.href, { push: false });
  };

  #onKeydown = (event) => {
    const sortOpen = this.#isSortOpen();

    if (event.key === 'Escape') {
      if (sortOpen) {
        event.preventDefault();
        this.#closeSort({ restoreFocus: true });
        return;
      }
      const drawer = this.#drawer;
      if (drawer?.open) {
        event.preventDefault();
        this.#closeDrawer();
      }
      return;
    }

    if (event.key === 'Tab' && sortOpen) {
      this.#closeSort();
      return;
    }

    const toggle = event.target instanceof Element
      ? event.target.closest('[data-darkrim-coll-sort-toggle]')
      : null;
    if (toggle && this.contains(toggle) && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      this.#openSort(event.key === 'ArrowUp' ? 'last' : 'selected');
      return;
    }

    const option = event.target instanceof Element
      ? event.target.closest('[data-darkrim-coll-sort-option]')
      : null;
    if (!option || !this.contains(option)) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#moveSortFocus(option, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#moveSortFocus(option, -1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.#moveSortFocus(option, 'first');
    } else if (event.key === 'End') {
      event.preventDefault();
      this.#moveSortFocus(option, 'last');
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#selectSort(option);
    }
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const sortToggle = target.closest('[data-darkrim-coll-sort-toggle]');
    if (sortToggle && this.contains(sortToggle)) {
      event.preventDefault();
      if (this.#isSortOpen()) this.#closeSort();
      else this.#openSort('selected');
      return;
    }

    const sortOption = target.closest('[data-darkrim-coll-sort-option]');
    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      this.#selectSort(sortOption);
      return;
    }

    const openTrigger = target.closest('[data-darkrim-coll-open]');
    if (openTrigger && this.contains(openTrigger)) {
      event.preventDefault();
      this.#openDrawer();
      return;
    }

    const closeTrigger = target.closest('[data-darkrim-coll-close]');
    if (closeTrigger && this.contains(closeTrigger)) {
      event.preventDefault();
      this.#closeDrawer();
      return;
    }

    const clearTrigger = target.closest('[data-darkrim-coll-clear]');
    if (clearTrigger && this.contains(clearTrigger)) {
      event.preventDefault();
      const url = clearTrigger.getAttribute('href');
      if (url) this.#load(url);
      return;
    }

    const ajaxLink = target.closest('[data-darkrim-coll-ajax]');
    if (ajaxLink instanceof HTMLAnchorElement && this.contains(ajaxLink) && ajaxLink.href) {
      event.preventDefault();
      this.#load(ajaxLink.href);
    }
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;
    if (!form.dataset.darkrimCollForm) return;
    event.preventDefault();
    this.#applyForm(form);
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-darkrim-coll-sort]')) {
      this.#applyFromSort(target);
      return;
    }

    if (target.matches('[data-darkrim-coll-price-min-range], [data-darkrim-coll-price-max-range]')) {
      this.#syncPriceFromRanges(target.closest('[data-darkrim-coll-price]'));
      if (!this.#isMobile) this.#scheduleApply(target.closest('form'));
      return;
    }

    if (target.matches('[data-darkrim-coll-price-min-input], [data-darkrim-coll-price-max-input]')) {
      this.#syncPriceFromInputs(target.closest('[data-darkrim-coll-price]'));
      if (!this.#isMobile) this.#scheduleApply(target.closest('form'));
      return;
    }

    if (target.matches('.darkrim-coll-check__input') && !this.#isMobile) {
      this.#applyForm(target.closest('form'));
    }
  };

  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-darkrim-coll-price-min-range], [data-darkrim-coll-price-max-range]')) {
      this.#syncPriceFromRanges(target.closest('[data-darkrim-coll-price]'));
    }
  };

  #openDrawer() {
    const drawer = this.#drawer;
    if (!(drawer instanceof HTMLDialogElement) || drawer.open) return;

    const active = document.activeElement;
    this.#drawerTrigger =
      active instanceof HTMLElement && this.contains(active)
        ? active
        : this.querySelector('[data-darkrim-coll-open]');

    this.#syncDrawerTop();
    window.addEventListener('resize', this.#syncDrawerTop);

    /* Non-modal show() stays out of the top layer so the sticky header remains visible. */
    if (typeof drawer.show === 'function') drawer.show();
    else drawer.setAttribute('open', '');

    lockScroll(drawer);
    this.querySelector('[data-darkrim-coll-open]')?.setAttribute('aria-expanded', 'true');

    if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = requestAnimationFrame(() => {
        this.#openFrame = 0;
        drawer.classList.add('is-open');
        const closeBtn = drawer.querySelector('[data-darkrim-coll-close]');
        if (closeBtn instanceof HTMLElement) closeBtn.focus();
      });
    });
  }

  #closeDrawer() {
    const drawer = this.#drawer;
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;

    if (this.#openFrame) {
      cancelAnimationFrame(this.#openFrame);
      this.#openFrame = 0;
    }

    if (!drawer.classList.contains('is-open')) {
      this.#finishClose(drawer);
      return;
    }

    drawer.classList.remove('is-open');
    onAnimationEnd(drawer, () => this.#finishClose(drawer), { subtree: false });
  }

  /**
   * @param {HTMLDialogElement} drawer
   */
  #finishClose(drawer) {
    if (!drawer.open) return;
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
  }

  #onDrawerClosed = () => {
    const drawer = this.#drawer;
    if (drawer instanceof HTMLDialogElement) {
      drawer.classList.remove('is-open');
      unlockScroll(drawer);
    }
    window.removeEventListener('resize', this.#syncDrawerTop);
    this.querySelector('[data-darkrim-coll-open]')?.setAttribute('aria-expanded', 'false');
    if (this.isConnected && this.#drawerTrigger instanceof HTMLElement) {
      this.#drawerTrigger.focus();
    }
    this.#drawerTrigger = null;
  };

  #sortToggle() {
    return this.querySelector('[data-darkrim-coll-sort-toggle]');
  }

  #sortList() {
    return this.querySelector('.darkrim-coll__sort-list');
  }

  #isSortOpen() {
    const list = this.#sortList();
    return Boolean(list && !list.hidden);
  }

  #onSortPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    const sort = this.querySelector('.darkrim-coll__sort');
    if (sort && sort.contains(target)) return;
    this.#closeSort();
  };

  #openSort(focus = 'selected') {
    const toggle = this.#sortToggle();
    const list = this.#sortList();
    if (!(toggle instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    list.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.#onSortPointerDown);

    const selected = list.querySelector('[aria-selected="true"]');
    const options = list.querySelectorAll('[data-darkrim-coll-sort-option]');
    const target =
      focus === 'last'
        ? options[options.length - 1]
        : selected || options[0];
    if (target instanceof HTMLElement) target.focus();
  }

  #closeSort({ restoreFocus = false } = {}) {
    const toggle = this.#sortToggle();
    const list = this.#sortList();
    if (!(list instanceof HTMLElement) || list.hidden) return;

    list.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.#onSortPointerDown);
    if (restoreFocus && toggle instanceof HTMLElement) toggle.focus();
  }

  #moveSortFocus(current, direction) {
    const list = this.#sortList();
    if (!(list instanceof HTMLElement) || !(current instanceof HTMLElement)) return;

    const options = [...list.querySelectorAll('[data-darkrim-coll-sort-option]')];
    if (options.length === 0) return;

    let next = options[0];
    if (direction === 'last') {
      next = options[options.length - 1];
    } else if (direction === 'first') {
      next = options[0];
    } else {
      const index = options.indexOf(current);
      const offset = typeof direction === 'number' ? direction : 0;
      next = options[(index + offset + options.length) % options.length];
    }
    if (next instanceof HTMLElement) next.focus();
  }

  #selectSort(option) {
    if (!(option instanceof HTMLElement)) return;
    const select = this.querySelector('[data-darkrim-coll-sort]');
    const value = option.getAttribute('data-value') || '';
    this.#closeSort({ restoreFocus: true });
    if (!(select instanceof HTMLSelectElement) || select.value === value) return;

    select.value = value;
    const current = this.querySelector('[data-darkrim-coll-sort-current]');
    if (current) current.textContent = option.textContent.trim();
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #applyFromSort(select) {
    if (!(select instanceof HTMLSelectElement)) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    this.#load(url.toString());
  }

  #scheduleApply(form) {
    window.clearTimeout(this.#applyTimer);
    this.#applyTimer = window.setTimeout(() => this.#applyForm(form), 360);
  }

  #applyForm(form) {
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || window.location.pathname;
    const url = new URL(action, window.location.origin);
    const data = new FormData(form);

    for (const [key, value] of data.entries()) {
      if (value === '' && (key.endsWith('.gte') || key.endsWith('.lte'))) continue;
      url.searchParams.append(key, String(value));
    }

    const sort = this.querySelector('[data-darkrim-coll-sort]');
    if (sort instanceof HTMLSelectElement && sort.value) {
      url.searchParams.set('sort_by', sort.value);
    }

    url.searchParams.delete('page');
    this.#load(url.toString());
  }

  async #load(url, { push = true } = {}) {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.delete('section_id');
    const sectionUrl = new URL(nextUrl);
    sectionUrl.searchParams.set('section_id', this.dataset.sectionId || '');

    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(sectionUrl.toString(), {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error('Collection section request failed');

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const next = parsed.querySelector('darkrim-collection');
      if (!(next instanceof HTMLElement)) throw new Error('Collection markup missing');

      if (push) window.history.pushState({}, '', nextUrl.toString());
      this.replaceWith(next);
    } catch (error) {
      window.location.assign(nextUrl.toString());
    }
  }

  #syncPriceRanges() {
    this.querySelectorAll('[data-darkrim-coll-price]').forEach((group) => this.#paintPrice(group));
  }

  #syncPriceFromRanges(group) {
    if (!(group instanceof HTMLElement)) return;
    const minRange = group.querySelector('[data-darkrim-coll-price-min-range]');
    const maxRange = group.querySelector('[data-darkrim-coll-price-max-range]');
    const minInput = group.querySelector('[data-darkrim-coll-price-min-input]');
    const maxInput = group.querySelector('[data-darkrim-coll-price-max-input]');
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    let min = Number(minRange.value);
    let max = Number(maxRange.value);
    if (min > max) {
      if (document.activeElement === minRange) max = min;
      else min = max;
      minRange.value = String(min);
      maxRange.value = String(max);
    }

    if (minInput instanceof HTMLInputElement) minInput.value = String(min);
    if (maxInput instanceof HTMLInputElement) maxInput.value = String(max);
    if (document.activeElement === minRange) {
      minRange.style.zIndex = '4';
      maxRange.style.zIndex = '3';
    } else {
      maxRange.style.zIndex = '4';
      minRange.style.zIndex = '2';
    }
    this.#paintPrice(group, min, max);
  }

  #syncPriceFromInputs(group) {
    if (!(group instanceof HTMLElement)) return;
    const minRange = group.querySelector('[data-darkrim-coll-price-min-range]');
    const maxRange = group.querySelector('[data-darkrim-coll-price-max-range]');
    const minInput = group.querySelector('[data-darkrim-coll-price-min-input]');
    const maxInput = group.querySelector('[data-darkrim-coll-price-max-input]');
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const floor = Number(group.dataset.rangeMin || minRange.min);
    const ceil = Number(group.dataset.rangeMax || maxRange.max);
    let min = this.#parseAmount(minInput instanceof HTMLInputElement ? minInput.value : '', floor);
    let max = this.#parseAmount(maxInput instanceof HTMLInputElement ? maxInput.value : '', ceil);
    min = Math.min(Math.max(min, floor), ceil);
    max = Math.min(Math.max(max, floor), ceil);
    if (min > max) max = min;

    minRange.value = String(min);
    maxRange.value = String(max);
    if (minInput instanceof HTMLInputElement) minInput.value = String(min);
    if (maxInput instanceof HTMLInputElement) maxInput.value = String(max);
    this.#paintPrice(group, min, max);
  }

  #paintPrice(group, minValue, maxValue) {
    if (!(group instanceof HTMLElement)) return;
    const minRange = group.querySelector('[data-darkrim-coll-price-min-range]');
    const maxRange = group.querySelector('[data-darkrim-coll-price-max-range]');
    const fill = group.querySelector('[data-darkrim-coll-price-fill]');
    const current = group.querySelector('[data-darkrim-coll-price-current]');
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const floor = Number(group.dataset.rangeMin || minRange.min);
    const ceil = Number(group.dataset.rangeMax || maxRange.max);
    const min = minValue ?? Number(minRange.value);
    const max = maxValue ?? Number(maxRange.value);
    const span = Math.max(ceil - floor, 1);
    const start = ((min - floor) / span) * 100;
    const end = ((max - floor) / span) * 100;
    const width = Math.max(end - start, 0);

    group.style.setProperty('--darkrim-coll-price-start', `${start}%`);
    group.style.setProperty('--darkrim-coll-price-end', `${end}%`);
    group.classList.toggle('is-at-min', Math.abs(min - floor) < 0.005);

    if (fill instanceof HTMLElement) {
      fill.style.left = `${start}%`;
      fill.style.width = `${width}%`;
    }
    if (current) current.textContent = this.#formatAmount(min);
  }

  #parseAmount(value, fallback) {
    const numeric = Number(String(value).replace(/[^\d.]/g, ''));
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  #formatAmount(value) {
    const symbol = this.dataset.currencySymbol || '$';
    return `${symbol}${Number(value).toLocaleString()}`;
  }
}

if (!customElements.get('darkrim-collection')) {
  customElements.define('darkrim-collection', DarkrimCollection);
}
